import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Info, Gauge, PiggyBank, Wallet } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(v);

const IMOVEL_OPTIONS = [
  { label: "0–1 imóvel", value: 0 },
  { label: "2–3 imóveis", value: 1 },
  { label: "4 ou mais", value: 2 },
];

export const RiskCalculator = () => {
  const [patrimonio, setPatrimonio] = useState(3_000_000);
  const [imoveis, setImoveis] = useState(1);
  const [empresa, setEmpresa] = useState(true);

  const { exposicao, economia, score } = useMemo(() => {
    const taxRate = Math.min(0.06 + imoveis * 0.02 + (empresa ? 0.04 : 0), 0.14);
    const exp = patrimonio * 0.5;
    const eco = patrimonio * taxRate;
    const sc = Math.min(
      Math.round(3 + Math.log10(patrimonio / 500_000) * 2.2 + imoveis + (empresa ? 2 : 0)),
      10
    );
    return { exposicao: exp, economia: eco, score: sc };
  }, [patrimonio, imoveis, empresa]);

  const fill = ((patrimonio - 500_000) / (50_000_000 - 500_000)) * 100;

  return (
    <section id="simulador" data-testid="risk-calculator-section" className="relative py-24 sm:py-32 bg-[#0E1728]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Simulador de risco patrimonial"
          title="Quanto do seu patrimônio está em jogo?"
          description="Ajuste os valores e veja uma estimativa ilustrativa da sua exposição na partilha e da economia tributária que uma estratégia jurídica estruturada pode gerar."
          align="center"
        />

        <Reveal delay={0.15} className="mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <div className="lg:col-span-3 border border-amber-500/15 bg-[#111C2E] rounded-2xl p-8 sm:p-10">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <label htmlFor="patrimonio-range" className="text-sm font-semibold text-slate-200">
                  Patrimônio total estimado
                </label>
                <span className="font-mono-num text-xl sm:text-2xl font-semibold gold-text" data-testid="calc-patrimonio-value">
                  {brl(patrimonio)}
                </span>
              </div>
              <input
                id="patrimonio-range"
                type="range"
                min={500_000}
                max={50_000_000}
                step={250_000}
                value={patrimonio}
                onChange={(e) => setPatrimonio(Number(e.target.value))}
                style={{ "--fill": `${fill}%` }}
                data-testid="calc-patrimonio-slider"
                className="gold-range w-full mt-6"
              />
              <div className="flex justify-between font-mono-num text-[10px] text-slate-500 mt-2">
                <span>R$ 500 mil</span>
                <span>R$ 50 mi+</span>
              </div>

              <div className="mt-9">
                <p className="text-sm font-semibold text-slate-200 mb-4">Quantidade de imóveis</p>
                <div className="grid grid-cols-3 gap-3" data-testid="calc-imoveis-group">
                  {IMOVEL_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setImoveis(o.value)}
                      data-testid={`calc-imoveis-${o.value}`}
                      className={`text-xs sm:text-sm font-medium rounded-xl border px-3 py-3.5 transition-all duration-300 ${
                        imoveis === o.value
                          ? "border-amber-400 bg-amber-400/10 text-amber-200"
                          : "border-slate-700 text-slate-400 hover:border-amber-500/40 hover:text-slate-200"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-9 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm font-semibold text-slate-200">
                  Possui empresa, quotas ou holding?
                </p>
                <div className="flex gap-3" data-testid="calc-empresa-group">
                  {[
                    { label: "Sim", value: true },
                    { label: "Não", value: false },
                  ].map((o) => (
                    <button
                      key={o.label}
                      onClick={() => setEmpresa(o.value)}
                      data-testid={`calc-empresa-${o.value ? "sim" : "nao"}`}
                      className={`text-sm font-medium rounded-full border px-6 py-2.5 transition-all duration-300 ${
                        empresa === o.value
                          ? "border-amber-400 bg-amber-400/10 text-amber-200"
                          : "border-slate-700 text-slate-400 hover:border-amber-500/40 hover:text-slate-200"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 flex flex-col gap-5">
              <motion.div
                key={`${exposicao}-${economia}-${score}`}
                initial={{ opacity: 0.4, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex-1 border border-amber-500/25 bg-gradient-to-b from-[#162238] to-[#0F1830] rounded-2xl p-8 flex flex-col justify-between"
                data-testid="calc-results"
              >
                <div className="space-y-7">
                  <div>
                    <p className="flex items-center gap-2 font-mono-num text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      <Wallet className="w-3.5 h-3.5 text-red-400" /> Exposição na partilha
                    </p>
                    <p className="font-mono-num text-2xl sm:text-3xl font-semibold text-red-300 mt-2" data-testid="calc-exposicao">
                      {brl(exposicao)}
                    </p>
                  </div>
                  <div className="h-px bg-amber-500/15" />
                  <div>
                    <p className="flex items-center gap-2 font-mono-num text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      <PiggyBank className="w-3.5 h-3.5 text-emerald-400" /> Economia tributária potencial
                    </p>
                    <p className="font-mono-num text-2xl sm:text-3xl font-semibold text-emerald-300 mt-2" data-testid="calc-economia">
                      {brl(economia)}
                    </p>
                  </div>
                  <div className="h-px bg-amber-500/15" />
                  <div>
                    <p className="flex items-center gap-2 font-mono-num text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      <Gauge className="w-3.5 h-3.5 text-amber-400" /> Índice de risco
                    </p>
                    <div className="flex items-end gap-2 mt-2">
                      <p className="font-mono-num text-2xl sm:text-3xl font-semibold text-amber-300" data-testid="calc-score">
                        {score}/10
                      </p>
                      <p className="text-xs text-slate-500 mb-1.5">
                        {score >= 8 ? "crítico — aja agora" : score >= 5 ? "elevado — planeje-se" : "moderado — previna-se"}
                      </p>
                    </div>
                  </div>
                </div>

                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href={waLink(WA_MESSAGES.calculator(brl(patrimonio), imoveis === 0 ? "0–1" : imoveis === 1 ? "2–3" : "4+", empresa, brl(exposicao), brl(economia)))}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calc-cta-whatsapp"
                  className="mt-8 inline-flex items-center justify-center gap-2.5 gold-surface text-[#0B1320] font-bold text-sm px-6 py-4 rounded-full shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_45px_rgba(212,175,55,0.5)] transition-shadow duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  Receber Análise no WhatsApp
                </motion.a>
              </motion.div>
            </div>
          </div>
        </Reveal>

        <p className="flex items-center justify-center gap-2 text-[11px] text-slate-500 mt-8 max-w-xl mx-auto text-center" data-testid="calc-disclaimer">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Estimativa ilustrativa, sem valor jurídico. Cada caso exige análise individual e confidencial.
        </p>
      </div>
    </section>
  );
};
